
import React from 'react';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { CheckCircle2, AlertCircle, RefreshCw, AlertTriangle } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { useVezgoConnectState } from './useVezgoConnectState';
import { Card } from "@/components/ui/card";
import { ConnectionProcessingState } from '@/providers/vezgo/constants';
import { Spinner } from "@/components/ui/Spinner";

export function VezgoConnectionStatus() {
  const { 
    isConnected, 
    connection, 
    error, 
    isProcessing,
    processingState,
    processingProgress,
    providerDisplayName
  } = useVezgoConnectState();

  // Format last checked time if available
  const lastChecked = connection?.lastChecked ? new Date(connection.lastChecked) : null;
  const lastCheckedFormatted = lastChecked ? 
    lastChecked.toLocaleString() : 
    'Never';

  // Show progress if processing
  const showProgress = isProcessing;
  
  // Format connection status message
  let statusMessage = isConnected ? 
    `Connected to ${providerDisplayName}` : 
    'Not connected to any cryptocurrency provider';
    
  // Add last checked information if available
  if (isConnected && lastChecked) {
    statusMessage += ` (Last updated: ${lastCheckedFormatted})`;
  }

  return (
    <div className="space-y-4">
      {/* Connection Status */}
      <Alert variant={isConnected ? "default" : "default"}>
        {isConnected ? (
          <CheckCircle2 className="h-4 w-4 text-green-500" />
        ) : (
          <AlertCircle className="h-4 w-4 text-gray-500" />
        )}
        <AlertTitle>{isConnected ? "Connected" : "Not Connected"}</AlertTitle>
        <AlertDescription>
          {statusMessage}
        </AlertDescription>
      </Alert>

      {/* Processing Progress */}
      {showProgress && (
        <Card className="p-4">
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <Spinner size="sm" className="mr-2" ariaLabel="Processing" />
                <span className="font-medium">
                  {processingState === ConnectionProcessingState.CONNECTING ? 'Connecting to provider...' : 
                   processingState === ConnectionProcessingState.LOADING_ACCOUNTS ? 'Loading accounts...' : 
                   processingState === ConnectionProcessingState.COMPLETE ? 'Connection complete' : 
                   'Processing...'}
                </span>
              </div>
              <span className="text-sm text-muted-foreground">{processingProgress}%</span>
            </div>

            <Progress value={processingProgress} className="h-2" />
          </div>
        </Card>
      )}

      {/* Error Message */}
      {error && !isProcessing && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Connection Error</AlertTitle>
          <AlertDescription>
            {error.message || 'An error occurred while connecting to the provider'}
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}

